import React, { useState, useRef, useEffect } from 'react';
import {
  Container, Box, Typography, TextField, Button, Stack, Chip, Paper, CircularProgress,
  Alert, LinearProgress, Divider, Link, Stepper, Step, StepLabel,
} from '@mui/material';
import { CheckCircle, ErrorOutline, UploadFile, RocketLaunch, Description, ArrowBack, AutoFixHigh, IosShare } from '@mui/icons-material';
import Footer from './Footer';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

interface Rec { title: string; detail: string }
interface Result {
  match_score: number;
  verdict: string;
  seniority: string;
  seniority_reason: string;
  content_sufficiency: string;
  matched_keywords: string[];
  missing_keywords: string[];
  recommendations: Rec[];
  resume_text: string;
  job_text: string;
}
interface BoostQ {
  id: string; question: string; why: string;
  type: 'multi' | 'single' | 'text';
  options: string[]; allow_detail: boolean; detail_prompt: string;
}
type Answer = { options: string[]; detail: string; repo?: string; course?: string };
const NEGATIVE_OPT = /^(not yet|no|n\/?a|none|never)\b/i;
// A picked option that should prompt for a repo/demo link, vs a class to describe.
const REPO_OPT = /personal|side project|open[\s-]?source|hackathon|github|portfolio/i;
const COURSE_OPT = /class|course|coursework|school|bootcamp|capstone/i;
interface SuggestedProject { title: string; build: string; skills: string[]; resume_block: string }
interface BoostResult {
  enhanced_resume: string; added: string[]; new_score: number; still_missing: string[];
  suggested_projects?: SuggestedProject[];
}

const SENIORITY_LABEL: Record<string, string> = {
  entry: 'Entry-level', junior: 'Junior', mid: 'Mid-level',
  senior: 'Senior', lead: 'Lead / Manager', executive: 'Executive',
};

const scoreColor = (s: number) => (s >= 75 ? '#2e7d32' : s >= 50 ? '#ed6c02' : '#d32f2f');
const looksLikeUrl = (s: string) => /^https?:\/\/\S+$/i.test(s.trim());
const okFile = (name: string) => /\.(pdf|txt)$/i.test(name);

// Resume-building projects (/projects/). For a thin/junior resume we suggest the
// one whose keywords best match the job's gap — turning "you lack experience" into
// "here's a concrete project (with a starter repo) to build it."
const RESUME_PROJECTS = [
  { slug: 'ml-prediction-project', title: 'End-to-End ML Prediction Project (Python)', kw: ['machine learning', 'ml', 'data science', 'data scientist', 'scikit-learn', 'sklearn', 'pandas', 'numpy', 'feature engineering', 'regression', 'classification', 'predictive', 'jupyter'] },
  { slug: 'ai-rag-qa-app', title: 'RAG Q&A App (Python / AI)', kw: ['ai', 'llm', 'rag', 'genai', 'generative', 'agentic', 'embeddings', 'vector', 'nlp', 'langchain', 'prompt'] },
  { slug: 'java-spring-microservice', title: 'Spring Boot REST Microservice (Java)', kw: ['java', 'spring', 'spring boot', 'jpa', 'hibernate', 'maven', 'junit', 'microservice'] },
  { slug: 'java-kafka-order-service', title: 'Event-Driven Order Service (Java + Kafka)', kw: ['kafka', 'rabbitmq', 'ibm mq', 'jms', 'messaging', 'event-driven', 'solace', 'activemq'] },
  { slug: 'fastapi-rest-api', title: 'Production-Style REST API (FastAPI)', kw: ['fastapi', 'flask', 'django', 'rest', 'api', 'backend', 'pytest', 'python'] },
  { slug: 'data-engineering-pipeline', title: 'Batch ETL Data Pipeline (Python + Airflow)', kw: ['etl', 'elt', 'data pipeline', 'data engineer', 'data engineering', 'airflow', 'spark', 'databricks', 'warehouse', 'ingestion'] },
  { slug: 'fullstack-dashboard-react-node', title: 'Full-Stack Dashboard (React + Node)', kw: ['react', 'javascript', 'typescript', 'node', 'frontend', 'vue', 'angular', 'css', 'full-stack', 'express', 'next'] },
  { slug: 'sql-analytics-dashboard', title: 'SQL Analytics + Dashboard', kw: ['sql', 'postgres', 'mysql', 'tableau', 'power bi', 'analytics', 'analyst', 'bi', 'dashboard', 'excel', 'snowflake'] },
  { slug: 'cloud-devops-deployment', title: 'Cloud Deploy: Terraform + CI/CD (DevOps)', kw: ['devops', 'sre', 'terraform', 'kubernetes', 'docker', 'ci/cd', 'cloud', 'aws', 'azure', 'gcp', 'infrastructure', 'observability', 'reliability', 'platform'] },
  { slug: 'mobile-app-project', title: 'Cross-Platform Mobile App (React Native)', kw: ['mobile', 'react native', 'android', 'ios', 'swift', 'kotlin', 'flutter', 'expo', 'jetpack', 'swiftui', 'app developer'] },
  { slug: 'network-automation-lab', title: 'Network Automation Lab (Python + Ansible)', kw: ['network', 'networking', 'cisco', 'routing', 'switching', 'bgp', 'ospf', 'netmiko', 'napalm', 'ansible', 'tcp/ip', 'firewall', 'lan', 'wan'] },
  { slug: 'appsec-security-project', title: 'AppSec: Find, Fix & Prevent OWASP Top 10', kw: ['security', 'cybersecurity', 'appsec', 'owasp', 'vulnerability', 'penetration', 'pentest', 'sast', 'dast', 'infosec', 'secure coding', 'threat', 'soc'] },
];
function pickResumeProject(missing: string[], matched: string[], jobText: string) {
  const hay = `${(missing || []).join(' ')} ${(matched || []).join(' ')} ${jobText || ''}`.toLowerCase();
  let best: typeof RESUME_PROJECTS[number] | null = null;
  let bestN = 0;
  for (const p of RESUME_PROJECTS) {
    const n = p.kw.filter((k) => hay.includes(k)).length;
    if (n > bestN) { bestN = n; best = p; }
  }
  return best;
}

const AtsScoreChecker: React.FC = () => {
  const [step, setStep] = useState(0); // 0 = résumé, 1 = job
  const [job, setJob] = useState('');
  const [resume, setResume] = useState('');
  const [fileName, setFileName] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const [honeypot, setHoneypot] = useState('');
  const fileRef = useRef<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState<Result | null>(null);

  // "Strengthen my resume" wizard (for thin/junior resumes that need ADDED content).
  type BoostStep = 'idle' | 'loadingQ' | 'answering' | 'applying' | 'done' | 'nogaps';
  const [boostStep, setBoostStep] = useState<BoostStep>('idle');
  const [boostError, setBoostError] = useState('');
  const [questions, setQuestions] = useState<BoostQ[]>([]);
  const [qi, setQi] = useState(0); // current wizard question
  const [answers, setAnswers] = useState<Record<string, Answer>>(() => {
    try { return JSON.parse(localStorage.getItem('ra_boost_answers_v2') || '{}'); } catch { return {}; }
  });
  const [boost, setBoost] = useState<BoostResult | null>(null);
  const [excludedProjects, setExcludedProjects] = useState<Set<number>>(new Set());
  const [shareMsg, setShareMsg] = useState('');
  const [beforePdf, setBeforePdf] = useState('');
  const [afterPdf, setAfterPdf] = useState('');
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState('');
  const [afterStale, setAfterStale] = useState(false);
  // Cache the layout-theme derived from the original PDF so re-renders skip the vision call.
  const themeRef = useRef<{ head: string; page_w: number; page_h: number } | null>(null);

  const fileToB64 = (f: File) => new Promise<string>((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result).split(',')[1] || '');
    r.onerror = reject;
    r.readAsDataURL(f);
  });

  type PdfOpts = { original_pdf_b64?: string; theme_head?: string; page_w?: number; page_h?: number; highlight_against?: string };
  const renderPdf = async (text: string, opts: PdfOpts = {}) => {
    const res = await fetch(`${API_BASE_URL}/api/resume-boost/pdf`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ text, ...opts }),
    });
    const data = await res.json();
    if (!res.ok || !data.pdf_base64) throw new Error(data?.detail || 'PDF failed');
    const bytes = Uint8Array.from(atob(data.pdf_base64), (c) => c.charCodeAt(0));
    const url = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
    return { url, theme: { head: data.theme_head as string, page_w: data.page_w, page_h: data.page_h } };
  };

  const generatePreviews = async (b: BoostResult, origText: string) => {
    setPdfError(''); setPdfLoading(true);
    try {
      const f = fileRef.current;
      const isPdf = !!f && /\.pdf$/i.test(f.name);
      const origB64 = isPdf ? await fileToB64(f as File) : undefined;
      // AFTER: clone the original PDF's layout (centered header, fonts, etc.), cache
      // the theme, and highlight what's new vs the original.
      const after = await renderPdf(composedResume(b),
        { highlight_against: origText, ...(origB64 ? { original_pdf_b64: origB64 } : {}) });
      themeRef.current = after.theme?.head ? after.theme : null;
      // BEFORE: the user's actual original PDF if they uploaded one; else render the
      // original text in the SAME derived/default theme so before & after match.
      let beforeUrl: string;
      if (isPdf) beforeUrl = URL.createObjectURL(f as File);
      else beforeUrl = (await renderPdf(origText, themeRef.current
        ? { theme_head: themeRef.current.head, page_w: themeRef.current.page_w, page_h: themeRef.current.page_h }
        : {})).url;
      setBeforePdf(beforeUrl); setAfterPdf(after.url); setAfterStale(false);
    } catch { setPdfError('Couldn’t format the PDFs — your text before/after is still below.'); }
    finally { setPdfLoading(false); }
  };

  const updateAfterPdf = async (b: BoostResult) => {
    setPdfError(''); setPdfLoading(true);
    try {
      const t = themeRef.current;
      const { url } = await renderPdf(composedResume(b), {
        highlight_against: result?.resume_text,
        ...(t ? { theme_head: t.head, page_w: t.page_w, page_h: t.page_h } : {}),
      });
      setAfterPdf((old) => { if (old) URL.revokeObjectURL(old); return url; });
      setAfterStale(false);
    } catch { setPdfError('Couldn’t update the PDF.'); }
    finally { setPdfLoading(false); }
  };

  // Final strengthened resume = enhanced (from real answers) + the suggested
  // projects the user keeps (they strike out any they won't build).
  const composedResume = (b: BoostResult): string => {
    const blocks = (b.suggested_projects || [])
      .filter((_, i) => !excludedProjects.has(i))
      .map((p) => p.resume_block).filter(Boolean);
    return b.enhanced_resume + (blocks.length ? `\n\nPROJECTS\n${blocks.join('\n\n')}` : '');
  };

  // Persist wizard answers so re-opening the checker doesn't make them start over.
  useEffect(() => {
    try { localStorage.setItem('ra_boost_answers_v2', JSON.stringify(answers)); } catch { /* ignore */ }
  }, [answers]);

  const shareScore = async (score: number, before?: number) => {
    setShareMsg('');
    try {
      const res = await fetch(`${API_BASE_URL}/api/score-card`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ score, before_score: before, seniority: result?.seniority }),
      });
      const data = await res.json();
      if (!res.ok || !data.path) throw new Error();
      const url = `${window.location.origin}${data.path}`;
      const text = before != null
        ? `I raised my resume’s ATS score from ${before} to ${score}/100 with Align Resume — check yours free:`
        : `I scored ${score}/100 on my resume — check yours free:`;
      if ((navigator as any).share) {
        try { await (navigator as any).share({ title: 'My resume ATS score', text, url }); return; } catch { /* fall through */ }
      }
      await navigator.clipboard.writeText(url);
      setShareMsg('Share link copied to clipboard!');
    } catch { setShareMsg('Couldn’t create a share link — try again.'); }
  };

  // Per-route SEO meta (Googlebot renders JS, so it reads these). Without this the
  // page would inherit the homepage title/description.
  useEffect(() => {
    const TITLE = 'Free ATS Resume Score Checker — Why You’re Not Getting Interviews | Align Resume';
    const DESC = 'Free ATS resume score checker — paste a job and your resume to see your match score, the keywords you’re missing, and exactly how to fix your resume for your experience level. No signup.';
    document.title = TITLE;
    const upsert = (selector: string, create: () => HTMLElement) => {
      let el = document.head.querySelector(selector) as HTMLElement | null;
      if (!el) { el = create(); document.head.appendChild(el); }
      return el;
    };
    const meta = (key: 'name' | 'property', val: string, content: string) => {
      const el = upsert(`meta[${key}="${val}"]`, () => {
        const m = document.createElement('meta'); m.setAttribute(key, val); return m;
      });
      el.setAttribute('content', content);
    };
    meta('name', 'description', DESC);
    meta('property', 'og:title', TITLE);
    meta('property', 'og:description', DESC);
    meta('name', 'twitter:title', TITLE);
    meta('name', 'twitter:description', DESC);
    const canon = upsert('link[rel="canonical"]', () => {
      const l = document.createElement('link'); l.setAttribute('rel', 'canonical'); return l;
    }) as HTMLLinkElement;
    canon.setAttribute('href', 'https://alignresume.com/ats-score');
  }, []);

  const acceptFile = (f: File) => {
    if (!okFile(f.name)) { setError('Please use a PDF or .txt resume.'); return; }
    setError('');
    fileRef.current = f;
    setFileName(f.name);
  };
  const clearFile = () => { fileRef.current = null; setFileName(''); };

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f) acceptFile(f);
  };

  const hasResume = !!resume.trim() || !!fileRef.current;

  const next = () => {
    if (!hasResume) { setError('Add your resume to continue — paste it or drop a PDF.'); return; }
    setError('');
    setStep(1);
  };

  const submit = async () => {
    setError('');
    if (!job.trim()) { setError('Paste the job posting (or its URL).'); return; }
    if (!hasResume) { setError('Add your resume first.'); setStep(0); return; }
    setLoading(true);
    setResult(null);
    setBoost(null); setBoostStep('idle'); setQuestions([]); setAnswers({});
    try {
      const fd = new FormData();
      if (looksLikeUrl(job)) fd.append('job_url', job.trim()); else fd.append('job_text', job);
      if (fileRef.current) fd.append('resume_file', fileRef.current);
      else fd.append('resume_text', resume);
      fd.append('company_website', honeypot); // honeypot
      const res = await fetch(`${API_BASE_URL}/api/ats-score`, { method: 'POST', body: fd });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || 'Something went wrong. Please try again.');
      setResult(data);
    } catch (e: any) {
      setError(e?.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const startBoost = async () => {
    if (!result) return;
    setBoostError(''); setBoostStep('loadingQ');
    try {
      const res = await fetch(`${API_BASE_URL}/api/resume-boost/questions`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          resume_text: result.resume_text, job_text: result.job_text,
          missing_keywords: result.missing_keywords, // only ask about real gaps
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || 'Couldn’t load questions.');
      const qs: BoostQ[] = data.questions || [];
      setQuestions(qs);
      setQi(0);
      setBoostStep(qs.length ? 'answering' : 'nogaps');
    } catch (e: any) { setBoostError(e?.message || 'Try again.'); setBoostStep('idle'); }
  };

  const applyBoost = async () => {
    if (!result) return;
    const ans = questions.map((q) => {
      const a = answers[q.id];
      if (!a) return null;
      const opts = (a.options || []).filter((o) => !NEGATIVE_OPT.test(o.trim()));
      const parts = [opts.join(', ')];
      if ((a.course || '').trim()) parts.push(`coursework: ${a.course!.trim()}`);
      if ((a.repo || '').trim()) parts.push(`link: ${a.repo!.trim()}`);
      if ((a.detail || '').trim()) parts.push(a.detail!.trim());
      const answer = parts.filter(Boolean).join(' — ');
      return answer ? { question: q.question, answer } : null;
    }).filter(Boolean) as { question: string; answer: string }[];
    if (!ans.length) { setBoostError('Pick at least one answer so we have something to add.'); return; }
    setBoostError(''); setBoostStep('applying');
    try {
      const res = await fetch(`${API_BASE_URL}/api/resume-boost/apply`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ resume_text: result.resume_text, job_text: result.job_text, answers: ans }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || 'Couldn’t strengthen the resume.');
      setBoost(data); setExcludedProjects(new Set()); setBoostStep('done');
      generatePreviews(data, result.resume_text); // formatted before/after PDFs
    } catch (e: any) { setBoostError(e?.message || 'Try again.'); setBoostStep('answering'); }
  };

  return (
    <Container maxWidth="md">
      <Box sx={{ my: 4 }}>
        <Typography variant="h3" component="h1" align="center" className="matrix-glow" gutterBottom>
          Free ATS Resume Score
        </Typography>
        <Typography variant="subtitle1" component="h2" align="center" sx={{ color: 'primary.main', mb: 1 }} className="matrix-cursor">
          &gt; See why your resume isn’t getting interviews — in seconds, no signup
        </Typography>
        <Typography variant="body2" align="center" color="text.secondary" sx={{ mb: 3 }}>
          Add your resume, then the job. We’ll score the match, find missing keywords, and tell you exactly how to fix it for your experience level.
        </Typography>

        <Paper variant="outlined" sx={{ p: 2.5, mb: 3 }}>
          <Stepper activeStep={step} sx={{ mb: 3 }}>
            <Step><StepLabel>Your résumé</StepLabel></Step>
            <Step><StepLabel>The job</StepLabel></Step>
          </Stepper>

          {step === 0 && (
            <Stack spacing={2}>
              <Box
                onDrop={onDrop}
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                sx={{
                  border: '2px dashed',
                  borderColor: dragOver ? 'primary.main' : 'divider',
                  bgcolor: dragOver ? 'action.hover' : 'transparent',
                  borderRadius: 2, p: 3, textAlign: 'center', transition: 'all .15s',
                }}
              >
                <UploadFile sx={{ fontSize: 36, color: dragOver ? 'primary.main' : 'text.disabled' }} />
                <Typography variant="body1" sx={{ mt: 1 }}>
                  {fileName ? `Selected: ${fileName}` : 'Drag & drop your résumé here'}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                  PDF or .txt
                </Typography>
                <Stack direction="row" spacing={1} justifyContent="center">
                  <Button component="label" variant="outlined" size="small" startIcon={<UploadFile />}>
                    {fileName ? 'Choose a different file' : 'Browse files'}
                    <input hidden type="file" accept=".pdf,.txt"
                      onChange={(e) => { const f = e.target.files?.[0]; if (f) acceptFile(f); }} />
                  </Button>
                  {fileName && (
                    <Button size="small" color="inherit" onClick={clearFile}>Remove</Button>
                  )}
                </Stack>
              </Box>

              <Divider>or paste it</Divider>

              <TextField
                label="Paste your résumé text"
                value={resume}
                onChange={(e) => setResume(e.target.value)}
                multiline minRows={6} fullWidth
                placeholder="Paste your résumé here…"
                disabled={!!fileRef.current}
                helperText={fileRef.current ? `Using uploaded file: ${fileName}` : ' '}
              />

              {error && <Alert severity="error">{error}</Alert>}
              <Button variant="contained" size="large" onClick={next} disabled={!hasResume}>
                Next: add the job →
              </Button>
            </Stack>
          )}

          {step === 1 && (
            <Stack spacing={2}>
              <Typography variant="body2" color="text.secondary">
                Résumé added{fileName ? ` (${fileName})` : ''}. Now paste the job you’re applying to.
              </Typography>
              <TextField
                label="Job posting (paste the text, or a URL)"
                value={job}
                onChange={(e) => setJob(e.target.value)}
                multiline minRows={6} fullWidth autoFocus
                placeholder="Paste the job description here…"
              />
              {error && <Alert severity="error">{error}</Alert>}
              <Stack direction="row" spacing={1}>
                <Button variant="text" startIcon={<ArrowBack />} onClick={() => { setError(''); setStep(0); }}>
                  Back
                </Button>
                <Button
                  variant="contained" size="large" onClick={submit} disabled={loading} sx={{ flex: 1 }}
                  startIcon={loading ? <CircularProgress size={18} /> : <CheckCircle />}
                >
                  {loading ? 'Scoring…' : 'Check my ATS score — free'}
                </Button>
              </Stack>
            </Stack>
          )}

          {/* Honeypot — hidden from humans, bots fill it */}
          <input
            type="text" value={honeypot} onChange={(e) => setHoneypot(e.target.value)}
            name="company_website" tabIndex={-1} autoComplete="off"
            style={{ position: 'absolute', left: '-9999px', width: 1, height: 1, opacity: 0 }}
            aria-hidden="true"
          />
        </Paper>

        {result && (
          <Paper variant="outlined" sx={{ p: 2.5, mb: 3 }}>
            <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1.5 }}>
              <Box sx={{ textAlign: 'center', minWidth: 96 }}>
                <Typography variant="h2" sx={{ color: scoreColor(result.match_score), lineHeight: 1 }}>
                  {result.match_score}
                </Typography>
                <Typography variant="caption" color="text.secondary">/ 100 match</Typography>
              </Box>
              <Box sx={{ flex: 1 }}>
                <LinearProgress
                  variant="determinate" value={result.match_score}
                  sx={{ height: 10, borderRadius: 1, mb: 1,
                    '& .MuiLinearProgress-bar': { backgroundColor: scoreColor(result.match_score) } }}
                />
                <Typography variant="body2">{result.verdict}</Typography>
                <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
                  {result.seniority && (
                    <Chip size="small" color="primary" variant="outlined"
                      label={`Reads as: ${SENIORITY_LABEL[result.seniority] || result.seniority}`} />
                  )}
                  {result.content_sufficiency === 'thin' && (
                    <Chip size="small" color="warning" label="Light on content" />
                  )}
                </Stack>
              </Box>
            </Stack>
            {result.seniority_reason && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                {result.seniority_reason}
              </Typography>
            )}
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
              <Button size="small" variant="outlined" startIcon={<IosShare />}
                onClick={() => shareScore(result.match_score)}>
                Share my score
              </Button>
              {shareMsg && <Typography variant="caption" color="text.secondary">{shareMsg}</Typography>}
            </Stack>

            {result.missing_keywords.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Missing keywords the job wants</Typography>
                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                  {result.missing_keywords.map((k) => (
                    <Chip key={k} size="small" icon={<ErrorOutline />} color="error" variant="outlined" label={k} />
                  ))}
                </Stack>
              </Box>
            )}
            {result.matched_keywords.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Keywords you already match</Typography>
                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                  {result.matched_keywords.map((k) => (
                    <Chip key={k} size="small" icon={<CheckCircle />} color="success" variant="outlined" label={k} />
                  ))}
                </Stack>
              </Box>
            )}

            {result.recommendations.length > 0 && (
              <Box sx={{ mb: 1 }}>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                  How to improve it{result.seniority ? ` (for a ${SENIORITY_LABEL[result.seniority] || result.seniority} candidate)` : ''}
                </Typography>
                <Stack spacing={1}>
                  {result.recommendations.map((r, i) => (
                    <Box key={i}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{r.title}</Typography>
                      <Typography variant="body2" color="text.secondary">{r.detail}</Typography>
                    </Box>
                  ))}
                </Stack>
              </Box>
            )}

            {(() => {
              const thin = result.content_sufficiency === 'thin'
                || result.seniority === 'entry' || result.seniority === 'junior';
              if (!thin) return null;
              const proj = pickResumeProject(result.missing_keywords, result.matched_keywords, result.job_text);
              return (
                <Box sx={{ mb: 1, p: 1.5, borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                  <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                    📦 Don’t have the experience yet? Build it.
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {proj ? (
                      <>Based on this job, build the <b>{proj.title}</b> — a real portfolio project with a
                        step-by-step guide and a starter repo. Then re-score and you’ll have genuine bullets to add.</>
                    ) : (
                      <>Build a real portfolio project to fill the gaps — each guide has milestones and a starter
                        repo, so you earn genuine résumé bullets.</>
                    )}
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {proj && (
                      <Button size="small" variant="contained" href={`/projects/${proj.slug}/`}>
                        Build this project
                      </Button>
                    )}
                    <Button size="small" variant="outlined" href="/projects/">
                      Browse all projects
                    </Button>
                  </Stack>
                </Box>
              );
            })()}

            <Divider sx={{ my: 2 }} />
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                Want this fixed automatically?
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Align Resume rewrites your resume to match each job — and can auto-apply to remote roles for you.
              </Typography>
              <Button variant="contained" size="large" href="/" startIcon={<RocketLaunch />}>
                Tailor my resume &amp; auto-apply — free
              </Button>
            </Box>
          </Paper>
        )}

        {result && (
          <Paper variant="outlined" sx={{ p: 2.5, mb: 3 }}>
            {boostStep === 'nogaps' && (
              <Stack direction="row" spacing={1} alignItems="center">
                <CheckCircle color="success" />
                <Typography variant="body2">
                  Good news — your resume already covers what this job asks for. Tailoring will surface it; no extra questions needed.
                </Typography>
              </Stack>
            )}
            {boostStep !== 'done' && boostStep !== 'nogaps' && (
              <>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                  <AutoFixHigh color="primary" />
                  <Typography variant="subtitle1">
                    {result.content_sufficiency === 'thin'
                      ? 'Your resume is light — let’s add real experience'
                      : 'Add experience you may have left off'}
                  </Typography>
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  Tailoring only reorders what’s already there. Answer a few quick questions and we’ll honestly weave in projects, coursework, and results you actually have — then re-score.
                </Typography>
                {boostError && <Alert severity="error" sx={{ mb: 1.5 }}>{boostError}</Alert>}
                {boostStep === 'idle' && (
                  <Button variant="contained" onClick={startBoost} startIcon={<AutoFixHigh />}>
                    Strengthen my resume →
                  </Button>
                )}
                {boostStep === 'loadingQ' && (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CircularProgress size={18} /><Typography variant="body2">Finding what to ask…</Typography>
                  </Stack>
                )}
                {boostStep === 'answering' && questions.length > 0 && (() => {
                  const idx = Math.min(qi, questions.length - 1);
                  const q = questions[idx];
                  const a: Answer = answers[q.id] || { options: [], detail: '' };
                  const isLast = idx >= questions.length - 1;
                  const setQ = (upd: Partial<Answer>) =>
                    setAnswers((prev) => ({ ...prev, [q.id]: { ...a, ...upd } }));
                  const wantRepo = a.options.some((o) => REPO_OPT.test(o));
                  const wantCourse = a.options.some((o) => COURSE_OPT.test(o));
                  const toggle = (opt: string) => {
                    const sel = a.options.includes(opt);
                    const options = q.type === 'single'
                      ? (sel ? [] : [opt])
                      : (sel ? a.options.filter((o) => o !== opt) : [...a.options, opt]);
                    setQ({ options });
                  };
                  return (
                    <Stack spacing={2}>
                      <LinearProgress variant="determinate" value={(idx / questions.length) * 100}
                        sx={{ height: 6, borderRadius: 1 }} />
                      <Typography variant="caption" color="text.secondary">
                        Question {idx + 1} of {questions.length}
                      </Typography>
                      <Typography variant="subtitle1">{q.question}</Typography>
                      {q.why && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: -1 }}>{q.why}</Typography>
                      )}
                      {q.options.length > 0 && (
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                          {q.options.map((opt) => {
                            const sel = a.options.includes(opt);
                            return (
                              <Chip key={opt} label={opt} clickable onClick={() => toggle(opt)}
                                color={sel ? 'primary' : 'default'} variant={sel ? 'filled' : 'outlined'}
                                icon={sel ? <CheckCircle /> : undefined} />
                            );
                          })}
                        </Stack>
                      )}
                      {wantCourse && (
                        <TextField
                          size="small" fullWidth multiline minRows={1}
                          label="Which class & what did you build/learn?"
                          placeholder="e.g., CS340 Databases — built a PostgreSQL inventory app"
                          value={a.course || ''} onChange={(e) => setQ({ course: e.target.value })}
                          helperText="We’ll reword it into a polished, job-aligned bullet."
                        />
                      )}
                      {wantRepo && (
                        <TextField
                          size="small" fullWidth
                          label="Project link (GitHub, live demo) — optional"
                          placeholder="github.com/you/project"
                          value={a.repo || ''} onChange={(e) => setQ({ repo: e.target.value })}
                          helperText="We’ll add it to the project on your resume."
                        />
                      )}
                      {q.allow_detail && (
                        <TextField
                          size="small" fullWidth multiline minRows={1}
                          label={q.detail_prompt || 'Add a detail (optional)'}
                          value={a.detail} onChange={(e) => setQ({ detail: e.target.value })}
                        />
                      )}
                      {boostError && <Alert severity="error">{boostError}</Alert>}
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Button variant="text" startIcon={<ArrowBack />} disabled={idx === 0}
                          onClick={() => { setBoostError(''); setQi(idx - 1); }}>
                          Back
                        </Button>
                        <Box sx={{ flex: 1 }} />
                        <Button variant="text" color="inherit"
                          onClick={() => { setBoostError(''); isLast ? applyBoost() : setQi(idx + 1); }}>
                          Skip
                        </Button>
                        {isLast ? (
                          <Button variant="contained" startIcon={<AutoFixHigh />} onClick={applyBoost}>
                            Strengthen my resume →
                          </Button>
                        ) : (
                          <Button variant="contained" onClick={() => { setBoostError(''); setQi(idx + 1); }}>
                            Next →
                          </Button>
                        )}
                      </Stack>
                      <Typography variant="caption" color="text.secondary">
                        Tap what applies — we only add what you tell us, nothing fabricated.
                      </Typography>
                    </Stack>
                  );
                })()}
                {boostStep === 'applying' && (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CircularProgress size={18} /><Typography variant="body2">Rewriting &amp; re-scoring…</Typography>
                  </Stack>
                )}
              </>
            )}
            {boostStep === 'done' && boost && (
              <>
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }} flexWrap="wrap" useFlexGap>
                  <Typography variant="h5" color="text.secondary" sx={{ textDecoration: 'line-through' }}>
                    {result.match_score}
                  </Typography>
                  <Typography variant="h5">→</Typography>
                  <Typography variant="h4" sx={{ color: scoreColor(boost.new_score) }}>{boost.new_score}</Typography>
                  <Chip color="success" label={`+${Math.max(0, boost.new_score - result.match_score)} points`} />
                  <Button size="small" variant="outlined" startIcon={<IosShare />}
                    onClick={() => shareScore(boost.new_score, result.match_score)}>
                    Share my jump
                  </Button>
                </Stack>
                {shareMsg && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>{shareMsg}</Typography>}
                {boost.added.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 0.5 }}>What we added (from your answers)</Typography>
                    <Stack component="ul" sx={{ pl: 2.5, m: 0 }} spacing={0.25}>
                      {boost.added.map((a, i) => (
                        <Typography key={i} component="li" variant="body2" color="text.secondary">{a}</Typography>
                      ))}
                    </Stack>
                  </Box>
                )}

                {boost.suggested_projects && boost.suggested_projects.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 0.25 }}>
                      Projects to build now (added to your resume)
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                      Start these now so you can speak to them — they’re added to your strengthened resume below. Strike out any you won’t do.
                    </Typography>
                    <Stack spacing={1}>
                      {boost.suggested_projects.map((sp, i) => {
                        const excluded = excludedProjects.has(i);
                        const toggle = () => {
                          setExcludedProjects((prev) => {
                            const n = new Set(prev); n.has(i) ? n.delete(i) : n.add(i); return n;
                          });
                          if (afterPdf) setAfterStale(true);
                        };
                        return (
                          <Box key={i} sx={{ p: 1.25, border: '1px dashed', borderColor: excluded ? 'divider' : 'success.main',
                            borderRadius: 1, opacity: excluded ? 0.5 : 1 }}>
                            <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="space-between">
                              <Box sx={{ flex: 1 }}>
                                <Typography variant="body2"
                                  sx={{ fontWeight: 600, textDecoration: excluded ? 'line-through' : 'none' }}>
                                  {sp.title}
                                </Typography>
                                <Typography variant="body2" color="text.secondary"
                                  sx={{ textDecoration: excluded ? 'line-through' : 'none' }}>
                                  {sp.build}
                                </Typography>
                                {sp.skills && sp.skills.length > 0 && (
                                  <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }} flexWrap="wrap" useFlexGap>
                                    {sp.skills.map((s) => (<Chip key={s} size="small" variant="outlined" label={s} />))}
                                  </Stack>
                                )}
                              </Box>
                              <Button size="small" color="inherit" onClick={toggle}>
                                {excluded ? 'Add back' : 'Strike out'}
                              </Button>
                            </Stack>
                          </Box>
                        );
                      })}
                    </Stack>
                  </Box>
                )}

                {/* Original vs strengthened — formatted PDFs, side by side */}
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Before &amp; after (formatted)</Typography>
                {pdfError && <Alert severity="warning" sx={{ mb: 1 }}>{pdfError}</Alert>}
                {pdfLoading && !beforePdf ? (
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                    <CircularProgress size={18} />
                    <Typography variant="body2">Formatting your résumés…</Typography>
                  </Stack>
                ) : (beforePdf || afterPdf) ? (
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="caption" color="text.secondary">Original</Typography>
                      {beforePdf && (
                        <iframe title="Original resume" src={beforePdf}
                          style={{ width: '100%', height: 480, border: '1px solid #bbb', borderRadius: 6 }} />
                      )}
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        Strengthened{afterStale ? ' (out of date — update below)' : ''}
                        {' · '}
                        <Box component="span" sx={{ bgcolor: 'rgba(76,175,80,0.22)', px: 0.5, borderRadius: 0.5 }}>
                          green = new/changed
                        </Box>
                      </Typography>
                      {afterPdf && (
                        <iframe title="Strengthened resume" src={afterPdf}
                          style={{ width: '100%', height: 480, border: '2px solid #2e7d32', borderRadius: 6 }} />
                      )}
                      {afterStale && (
                        <Button size="small" sx={{ mt: 0.5 }} disabled={pdfLoading}
                          startIcon={pdfLoading ? <CircularProgress size={14} /> : <AutoFixHigh />}
                          onClick={() => updateAfterPdf(boost)}>
                          Update strengthened PDF
                        </Button>
                      )}
                    </Box>
                  </Stack>
                ) : (
                  // Fallback if PDF rendering failed: show the strengthened text.
                  <TextField
                    label="Strengthened resume" value={composedResume(boost)}
                    multiline minRows={8} maxRows={20} fullWidth InputProps={{ readOnly: true }}
                    sx={{ mb: 2, '& .MuiOutlinedInput-notchedOutline': { borderColor: 'success.main' } }}
                  />
                )}

                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    Save it, tailor it to every job you apply to, and let Align Resume auto-apply for you.
                  </Typography>
                  <Button
                    variant="contained" size="large" startIcon={<RocketLaunch />}
                    onClick={() => {
                      try { localStorage.setItem('ra_boosted_resume', composedResume(boost)); } catch { /* ignore */ }
                      window.location.href = '/';
                    }}
                  >
                    Continue free →
                  </Button>
                </Box>
              </>
            )}
          </Paper>
        )}

        <Typography variant="body2" align="center" color="text.secondary">
          <Link href="/guides/" color="primary" underline="hover">Why am I not getting interviews?</Link>
          {' · '}
          <Link href="/" color="primary" underline="hover"><Description sx={{ fontSize: 14, verticalAlign: 'text-bottom' }} /> Full resume tailoring</Link>
        </Typography>
        <Typography variant="body2" align="center" color="text.secondary" sx={{ mt: 1 }}>
          Read next:{' '}
          <Link href="/blog/beat-applicant-tracking-systems-ats.html" color="primary" underline="hover">How to beat the ATS with keyword matching</Link>
          {' · '}
          <Link href="/blog/how-to-tailor-resume-to-job-description-with-ai.html" color="primary" underline="hover">How to tailor your resume to a job description with AI</Link>
        </Typography>
      </Box>
      <Footer />
    </Container>
  );
};

export default AtsScoreChecker;
